import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    PromotionType,
    Promotion as PromotionTypeInterface,
    RefreshOptions,
} from "../../components/promotions/types";
import NoItems from "../../components/misc/NoItems";
import LoadingScreen from "../LoadingScreen";
import { getAuthHeader } from "../../utils/authHeader";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import axios from "axios";
import Promotion from "../../components/promotions/Promotion";
import { Assets } from "../../types/asset-type";
import { SocketProvider } from "../../context/SocketContext";
import { useLocation } from "react-router-dom";

const PromotionsScreen = () => {
    const location = useLocation();
    const state = location.state;

    const createPromotion = state?.createPromotion;

    const [loading, setLoading] = useState(false);
    const [promotion, setPromotion] = useState<PromotionTypeInterface | null>(
        null
    );
    const [originalPromotion, setOriginalPromotion] =
        useState<PromotionTypeInterface | null>(null);
    const [isEditing, setIsEditing] = useState(createPromotion || false);
    const [assets, setAssets] = useState<Assets[]>([]);

    // State to manage the active tab: "categories" or "promotion"
    const [activeTab, setActiveTab] = useState<"categories" | "promotion">(
        "categories"
    );

    // State to manage the active promotion type when "assets" tab is active
    const [activePromotionType, setActivePromotionType] =
        useState<PromotionType>(promotion?.types[0] || PromotionType.Mortgage);

    const [showSaveWarning, setShowSaveWarning] = useState(false);

    // Memoize fetchPromotions to prevent unnecessary re-creations
    const fetchPromotions = useCallback(async (options?: RefreshOptions) => {
        const silent = options?.silent ?? false;
        if (!silent) setLoading(true);

        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const response = await axios.get(`${backendUrl}/promotions`, {
                headers,
            });

            if (!response.data) {
                // If no data is returned, set promotion to null
                setPromotion(null);
            }

            if (response.status !== 200) {
                throw new Error(
                    "Failed to fetch promotions. Please try again later."
                );
            } else {
                const data =
                    response.data.id === null ||
                    response.data.id === undefined ||
                    response.data.id.length <= 0
                        ? null
                        : response.data;
                setPromotion(data);
                setOriginalPromotion(data);
            }
        } catch (error: unknown) {
            console.error("Failed to fetch promotions:", error);
            toast.error(
                <CustomToast
                    message="Failed to fetch promotions. Please try again later."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Memoize fetchAssets to include dependencies
    const fetchAssets = useCallback(async (options?: RefreshOptions) => {
        if (!originalPromotion?.id) {
            return;
        }
        const silent = options?.silent ?? false;
        if (!silent) setLoading(true);

        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const response = await axios.get(
                `${backendUrl}/promotions/media/`,
                {
                    headers,
                }
            );

            if (response.data) {
                if (Array.isArray(response.data)) {
                    setAssets(response.data);
                } else if (
                    response.data.assets &&
                    Array.isArray(response.data.assets)
                ) {
                    setAssets(response.data.assets);
                } else {
                    console.error("Assets data is not an array", response.data);
                    setAssets([]);
                }
            }
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 404) {
                    console.error("No assets found for promotion", error);
                } else {
                    console.error("Error fetching assets:", error);
                    toast.error(
                        <CustomToast
                            message="Failed to load assets."
                            type="error"
                        />,
                        {
                            autoClose: 3000,
                        }
                    );
                }
            } else {
                console.error("Error fetching assets:", error);
                toast.error(
                    <CustomToast
                        message="Failed to load assets."
                        type="error"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [originalPromotion]);

    // Silence watcher: poll fetchPromotions every 5s when campaign is PROCESSING,
    // as a fallback in case the WebSocket event is missed.
    const silenceWatcherRef = useRef<number | null>(null);

    const stopSilenceWatcher = useCallback(() => {
        if (silenceWatcherRef.current !== null) {
            clearTimeout(silenceWatcherRef.current);
            silenceWatcherRef.current = null;
        }
    }, []);

    const startSilenceWatcher = useCallback(() => {
        stopSilenceWatcher();
        if (promotion?.campaignStatus !== "PROCESSING") return;
        silenceWatcherRef.current = window.setTimeout(() => {
            fetchPromotions({ silent: true }).catch(console.error);
        }, 5000);
    }, [promotion?.campaignStatus, fetchPromotions, stopSilenceWatcher]);

    useEffect(() => {
        startSilenceWatcher();
        return stopSilenceWatcher;
    }, [startSilenceWatcher, stopSilenceWatcher]);

    // Effect to fetch promotions on mount
    useEffect(() => {
        fetchPromotions();
    }, [fetchPromotions]);

    // Re-runs on every promotion refresh (socket event / poll) so media generated
    // after load shows up without a manual reload. Silent: the screen-level loader
    // would unmount the whole tree and read as a page reload.
    useEffect(() => {
        if (originalPromotion) {
            fetchAssets({ silent: true });
        }
    }, [originalPromotion, fetchAssets]);

    const handleCreatePromotion = () => {
        setIsEditing(true);
    };

    return (
        <SocketProvider>
            <div
                className="w-full h-full"
                style={
                    (!promotion || promotion === null) && !isEditing
                        ? { height: "calc(100vh - 10rem)", overflow: "hidden" }
                        : {}
                }
            >
                {loading && <LoadingScreen />}
                {!loading &&
                (!promotion || promotion === null) &&
                !isEditing ? (
                    <NoItems
                        title="No Promotion"
                        description="You have not created a promotion yet."
                        descriptionTwo="Click the button below to create a new promotion."
                        buttonText="Create Promotion"
                        clickHandler={handleCreatePromotion}
                    />
                ) : null}
                {!loading && (
                    <div>
                        <Promotion
                            promotion={promotion}
                            setPromotion={setPromotion}
                            isEditing={isEditing}
                            setIsEditing={setIsEditing}
                            assets={assets}
                            setLoading={setLoading}
                            fetchAssets={fetchAssets}
                            fetchPromotions={fetchPromotions}
                            originalPromotion={originalPromotion}
                            setOriginalPromotion={setOriginalPromotion}
                            showSaveWarning={showSaveWarning}
                            setShowSaveWarning={setShowSaveWarning}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            activePromotionType={activePromotionType}
                            setActivePromotionType={setActivePromotionType}
                        />
                    </div>
                )}
            </div>
        </SocketProvider>
    );
};

export default PromotionsScreen;
